/**
 * Server-Side Full Video Generator Hook
 * Uses FFmpeg API service instead of browser WASM
 * Pipeline: Get Videos → Normalize (API) → Concatenate (API) → Download/Upload → Create Record
 */

import { useCallback, useReducer, useRef } from 'react';
import { useAction } from 'convex/react';
import pLimit from 'p-limit';
import { api } from '../../convex/_generated/api';
import type {
  IFullVideoGeneratorAdapter,
  IFullVideoGeneratorState,
  IUseFullVideoGeneratorReturn,
  FullVideoGeneratorStatus,
} from '@/@types/fullVideoGenerator';
import type { Id } from '../../convex/_generated/dataModel';

// ============================================
// State Management
// ============================================

type ServerFullVideoGeneratorAction =
  | { type: 'START'; totalVideos: number }
  | { type: 'SET_STATUS'; status: FullVideoGeneratorStatus }
  | { type: 'SET_PROGRESS'; progress: number; videosProcessed?: number; totalVideos?: number }
  | { type: 'SUCCESS'; storageId: Id<'_storage'> }
  | { type: 'ERROR'; error: string }
  | { type: 'RESET' };

// FFmpeg API has 5 workers available for parallel processing
const NORMALIZE_CONCURRENCY_LIMIT = 5;

const initialState: IFullVideoGeneratorState = {
  status: 'idle',
  progress: 0,
  error: null,
  videosProcessed: 0,
  totalVideos: 0,
  resultStorageId: null,
};

function reducer(
  state: IFullVideoGeneratorState,
  action: ServerFullVideoGeneratorAction
): IFullVideoGeneratorState {
  switch (action.type) {
    case 'START':
      return {
        ...initialState,
        status: 'downloading',
        totalVideos: action.totalVideos,
      };
    case 'SET_STATUS':
      return { ...state, status: action.status };
    case 'SET_PROGRESS':
      return {
        ...state,
        progress: action.progress,
        videosProcessed: action.videosProcessed ?? state.videosProcessed,
        totalVideos: action.totalVideos ?? state.totalVideos,
      };
    case 'SUCCESS':
      return {
        ...state,
        status: 'completed',
        progress: 100,
        resultStorageId: action.storageId,
      };
    case 'ERROR':
      return { ...state, status: 'error', error: action.error };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// ============================================
// Hook Implementation
// ============================================

export function useServerFullVideoGenerator(
  adapter: IFullVideoGeneratorAdapter
): IUseFullVideoGeneratorReturn {
  const [state, dispatch] = useReducer(reducer, initialState);
  const abortedRef = useRef(false);

  // Convex actions for FFmpeg API
  const normalizeVideo = useAction(api.ffmpegApi.normalizeVideo);
  const concatenateVideos = useAction(api.ffmpegApi.concatenateVideos);
  const transferToStorage = useAction(api.ffmpegApi.transferToStorage);

  const generateFullVideo = useCallback(async () => {
    abortedRef.current = false;

    // Dispatch START immediately to show loader right away
    dispatch({ type: 'START', totalVideos: 0 });

    try {
      // Step 1: Get videos to concatenate (0%)
      console.warn(`[useServerFullVideoGenerator] Fetching videos for ${adapter.videoType}`);
      const videos = await adapter.getVideosForConcatenation();

      if (videos.length === 0) {
        dispatch({ type: 'ERROR', error: 'No videos to concatenate' });
        return;
      }

      // Update with actual total videos count (without resetting state)
      dispatch({
        type: 'SET_PROGRESS',
        progress: 0,
        videosProcessed: 0,
        totalVideos: videos.length,
      });

      console.warn(
        `[useServerFullVideoGenerator] Starting server-side processing of ${videos.length} videos`,
        { videoType: adapter.videoType }
      );

      // Step 2: Normalize videos in parallel via API (0-60% progress)
      dispatch({ type: 'SET_STATUS', status: 'normalizing' });

      const limit = pLimit(NORMALIZE_CONCURRENCY_LIMIT);
      let completed = 0;

      console.warn(
        `[useServerFullVideoGenerator] Starting parallel normalization with concurrency limit ${NORMALIZE_CONCURRENCY_LIMIT}`
      );

      const normalizePromises = videos.map((video, index) =>
        limit(async () => {
          if (abortedRef.current) {
            throw new Error('Operation aborted');
          }

          console.warn(
            `[useServerFullVideoGenerator] Normalizing video ${index + 1}/${videos.length}`,
            { duration: video.voiceoverDurationSeconds, url: video.videoUrl }
          );

          const result = await normalizeVideo({
            videoUrl: video.videoUrl,
            duration: video.voiceoverDurationSeconds,
            ...(adapter.targetResolution && {
              width: adapter.targetResolution.width,
              height: adapter.targetResolution.height,
            }),
          });

          completed++;
          const normalizeProgress = Math.round((completed / videos.length) * 60);
          dispatch({
            type: 'SET_PROGRESS',
            progress: normalizeProgress,
            videosProcessed: completed,
          });

          console.warn(
            `[useServerFullVideoGenerator] Video ${index + 1} normalized (${completed}/${videos.length} complete)`,
            {
              resultUrl: result.resultUrl,
              fileSizeBytes: result.fileSizeBytes,
              processingTimeMs: result.processingTimeMs,
            }
          );

          return { index, result, id: video.id };
        })
      );

      const results = await Promise.all(normalizePromises);

      // Sort by original index to maintain video order for concatenation
      results.sort((a, b) => a.index - b.index);
      const normalizedUrls = results.map(r => r.result.resultUrl);
      const sourceVideoIds = results.map(r => r.id);

      // Step 3: Concatenate normalized videos via API (60-85% progress)
      if (abortedRef.current) {
        throw new Error('Operation aborted');
      }

      dispatch({ type: 'SET_STATUS', status: 'concatenating' });
      dispatch({ type: 'SET_PROGRESS', progress: 60 });

      console.warn(
        `[useServerFullVideoGenerator] Concatenating ${normalizedUrls.length} normalized videos`
      );

      const concatenateResult = await concatenateVideos({
        videoUrls: normalizedUrls,
      });

      console.warn(`[useServerFullVideoGenerator] Concatenation complete`, {
        resultUrl: concatenateResult.resultUrl,
        fileSizeBytes: concatenateResult.fileSizeBytes,
        processingTimeMs: concatenateResult.processingTimeMs,
      });

      dispatch({ type: 'SET_PROGRESS', progress: 85 });

      // Step 4: Transfer from R2 to Convex storage (server-side, bypasses CORS) (85-95% progress)
      if (abortedRef.current) {
        throw new Error('Operation aborted');
      }

      dispatch({ type: 'SET_STATUS', status: 'uploading' });

      console.warn(
        `[useServerFullVideoGenerator] Transferring final video from R2 to Convex storage (server-side)`
      );

      // Use server-side transfer to bypass CORS
      const transferResult = await transferToStorage({
        sourceUrl: concatenateResult.resultUrl,
        contentType: 'video/mp4',
      });

      console.warn(
        `[useServerFullVideoGenerator] Transfer complete (${transferResult.fileSizeBytes} bytes)`
      );

      dispatch({ type: 'SET_PROGRESS', progress: 95 });

      const uploadResult = {
        storageId: transferResult.storageId as Id<'_storage'>,
      };

      // Step 5: Create final video record via adapter (95-100% progress)
      if (abortedRef.current) {
        throw new Error('Operation aborted');
      }

      console.warn(`[useServerFullVideoGenerator] Creating final video record`);

      // Calculate approximate duration from individual videos
      const totalDuration = videos.reduce((sum, video) => sum + video.voiceoverDurationSeconds, 0);

      await adapter.createFinalVideoRecord({
        storageId: uploadResult.storageId,
        durationSeconds: totalDuration,
        fileSizeBytes: concatenateResult.fileSizeBytes,
        sourceVideoIds,
      });

      dispatch({ type: 'SUCCESS', storageId: uploadResult.storageId });

      console.warn(`[useServerFullVideoGenerator] Full video generation complete`, {
        storageId: uploadResult.storageId,
        durationSeconds: totalDuration,
        fileSizeBytes: concatenateResult.fileSizeBytes,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Video generation failed';
      console.error(`[useServerFullVideoGenerator] Error:`, message);
      dispatch({ type: 'ERROR', error: message });
      throw err;
    }
  }, [adapter, normalizeVideo, concatenateVideos, transferToStorage]);

  const abort = useCallback(() => {
    // Note: Server-side jobs cannot be truly aborted (they continue on the server)
    // This only updates local state to stop further processing
    console.warn(`[useServerFullVideoGenerator] Abort requested (note: server jobs will continue)`);
    abortedRef.current = true;
    dispatch({ type: 'ERROR', error: 'Operation cancelled' });
  }, []);

  const reset = useCallback(() => {
    console.warn(`[useServerFullVideoGenerator] Reset`);
    abortedRef.current = false;
    dispatch({ type: 'RESET' });
  }, []);

  return {
    state,
    generateFullVideo,
    abort,
    reset,
  };
}
